import { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { playerService, tenderService } from "../../services/api";
import CustomDropdown from "../../components/common/CustomDropdown";
import { useMediaQuery } from "../../hooks/useMediaQuery";
// Shared, null-safe formatters — the same ones the request cards use
// (AgentTenders card / TenderCard), so the pitch-page header can never
// diverge from the card again (monthly salary label, humanised foot,
// "Free transfer" for a null fee, "-" for absent stats).
import {
  formatFeeBlock,
  formatFoot,
  formatSalary,
  getSalaryKind,
} from "../../utils/tenderFormatters";
// The platform's one number grouping (apostrophe, locale-independent), so the
// fee INPUT below is separated exactly like the fee DISPLAY above it.
import { formatGroupedNumber } from "../../utils/currencyFormatter";
// Same two fallbacks the request cards already use: PlayerAvatar for people
// (common/PlayerAvatar.jsx — grey initials on null AND on a 404), the shared
// clubInitials badge for a club without a linked Wyscout crest.
import PlayerAvatar from "../../components/common/PlayerAvatar";
import { clubInitials } from "../../utils/clubInitials";

const formatPosition = (position) => {
  return position
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// The transfer-fee INPUT mask. It must group exactly the way the fee is
// PRINTED back — the agent types into this field and then reads the result on
// the pitch card, and "12,500,000" going in against "12'500'000" coming out
// would look like the platform had changed the number (Marin, 2026-08-02:
// "für jede Währung Apostrophen ' verwenden").
//
// So it delegates to formatGroupedNumber, the same helper
// formatCurrencyWithSymbol groups with. The digits are stripped first: only
// whole units are typeable here, and `handleTransferFeeChange` stores the bare
// digit string, so what is submitted is never the masked text.
const formatCurrency = (value) => {
  if (!value) return "";
  // Remove all non-numeric characters
  const numericValue = value.replace(/[^0-9]/g, "");
  return formatGroupedNumber(numericValue);
};

// Currency choices for the transfer-fee dropdown. EUR / USD / GBP / CHF, EUR
// first (Marin, 2026-08-01: "INR bitte löschen, Euro USD Pfund — aber Euro ist
// Default"; follow-up the same day: "Franken kannst du reinnehmen ins
// Backend!"). Mirrors Pitch.transfer_fee_currency's choices on the backend —
// keep the two lists and their ORDER in step; this array is what the dropdown
// renders, so its order is the order the agent sees.
//
// Both the mobile and the desktop variant of this form read THIS array and the
// single `selectedCurrency` state below, so the two can never offer different
// currencies — see the isMobile branch around the Transfer Fee section.
const CURRENCY_CHOICES = [
  { value: "EUR", label: "EUR - Euro" },
  { value: "USD", label: "USD - US Dollar" },
  { value: "GBP", label: "GBP - British Pound" },
  { value: "CHF", label: "CHF - Swiss Franc" },
];

// The form's pre-selection. This is the ONLY place EUR is a default: the model
// field stays nullable without one, because a Free Player pitch must store no
// currency at all (it stores no fee either, and the free-agent fallback keys on
// exactly that absence).
const DEFAULT_CURRENCY = "EUR";

// Copy for the fee section. Four constants rather than four pairs of literals,
// because this form exists TWICE (mobile + desktop, see the `isMobile ?` branch
// around the Transfer Fee section) and the two must say the same thing.
//
// A request marked "free players only" asks for players who cost nothing, and
// on such a request this form does not ask for a fee at all — no amount, no
// currency, nothing to disable or pre-fill (Marin, 2026-08-01: "da bleibt
// einfach das Ablösedings weg", "du sollst einfach die Ablöseoption vom Berater
// sperren, er kann nicht mitsenden"). Removing the inputs IS the measure: with
// no field on screen there is nothing to send. What stays in the section is the
// pitch message, so the subtitle carries the short explanation — one calm
// sentence where the fee block used to be, not a second warning further down.
const FEE_SECTION_TITLE = "Transfer Fee Details";
const FEE_SECTION_SUBTITLE = "Add transfer fee details for this pitch";
const FREE_ONLY_SECTION_TITLE = "Pitch Message";
const FREE_ONLY_SECTION_SUBTITLE =
  "This request is for free players only, so no transfer fee applies.";

const PitchPlayer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenderId } = useParams();
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  // Why its own state and not the page's `error`: that one belongs to the
  // player-list fetch and renders in the list panel, which is behind the
  // modal overlay at the moment this message has to be read.
  const [pitchError, setPitchError] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isPlayerAccordionOpen, setIsPlayerAccordionOpen] = useState(true);
  const [isDocumentAccordionOpen, setIsDocumentAccordionOpen] = useState(false);
  const [isTransferFeeAccordionOpen, setIsTransferFeeAccordionOpen] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pitchContent, setPitchContent] = useState("");
  const [fileForApi, setFileForApi] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [transferFee, setTransferFee] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState(DEFAULT_CURRENCY);
  // Free Player toggle: when on, the Currency + Transfer Fee inputs go
  // disabled and we clear their values so the submit payload omits both
  // (the backend's transfer_fee / transfer_fee_currency fields are
  // already nullable, so 'free agent' is just expressed as the absence
  // of a fee — no schema change needed).
  const [isFreePlayer, setIsFreePlayer] = useState(false);
  const handleFreePlayerToggle = (nextValue) => {
    setIsFreePlayer(nextValue);
    if (nextValue) {
      // Clear what the user may have typed so the visible state matches
      // the submit payload and the user doesn't get a surprise when
      // toggling back off.
      setTransferFee("");
    }
  };

  // Check if screen is mobile
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Get tender details from navigation state when we arrived via the
  // card button; deep links / refreshes have no location.state, so the
  // tender is then fetched by :tenderId below instead of bouncing the
  // user back to the request list.
  const [tenderData, setTenderData] = useState(
    location.state?.tenderDetails || null
  );
  const [tenderLoading, setTenderLoading] = useState(
    !location.state?.tenderDetails
  );

  // Does this request accept only players who cost nothing? Then the fee block
  // below is not rendered at all — see the copy constants at the top of the
  // file. `only_free_players` travels on every serializer this page reads
  // (TenderAgentSerializer for the list card that fills location.state,
  // TenderClubDetailSerializer for the deep-link fetch), so both entry paths
  // answer this the same way.
  //
  // The 'Free Player' switch is GONE on such a request rather than shown fixed
  // on: its only job is to answer "is there a fee?", the request has already
  // answered it, and there is no fee input left for it to enable or disable. A
  // switch the agent cannot move invites "why is this locked?", and one that
  // stayed movable would let them contradict the request they are answering.
  // The value it would have carried is sent as a constant below.
  const isFreePlayersOnlyRequest = Boolean(tenderData?.only_free_players);

  // Header fee row — same branch logic as the request card (buy →
  // maximum_transfer_fee or "Free transfer", loan → loan fee, else
  // market value). Returns { label: null, value: null } when no tender.
  const feeBlock = formatFeeBlock(tenderData);

  // No navigation state (direct navigation / F5): fetch the request by
  // id via the existing detail endpoint (tenderService.getSingleTender,
  // also used by useViewPitches / EditTender). Only redirect back to
  // the list when the fetch itself fails (e.g. 404).
  useEffect(() => {
    if (location.state?.tenderDetails) return;
    if (!tenderId) {
      navigate("/agent/tenders");
      return;
    }
    let cancelled = false;
    const fetchTender = async () => {
      setTenderLoading(true);
      try {
        const response = await tenderService.getSingleTender(tenderId);
        if (cancelled) return;
        if (response.success && response.data) {
          setTenderData(response.data);
        } else {
          navigate("/agent/tenders");
        }
      } catch (err) {
        if (!cancelled) {
          navigate("/agent/tenders");
        }
      } finally {
        if (!cancelled) {
          setTenderLoading(false);
        }
      }
    };
    fetchTender();
    return () => {
      cancelled = true;
    };
  }, [location.state, tenderId, navigate]);

  // Add this effect to fetch players
  useEffect(() => {
       const controller = new AbortController();

    const fetchPlayers = async () => {
      setLoading(true);
      setError(null); // Clear any previous errors
      try {
        const response = await playerService.getPlayers(
          new URLSearchParams(),
          controller.signal
        );
        console.log(response, "response");
        console.log("Response data:", response.data);
        console.log("Response success:", response.success);

        if (response.success) {
          // For now, show all players regardless of active status
          // TODO: Implement proper filtering logic based on requirements
          console.log("All players:", response.data);
          console.log("Players length:", response.data.length);
          setPlayers(response.data);
        } else {
          console.log("Response error:", response.error);
          setError(response.error);
        }
      } catch (err) {
        console.log(err, "err");
        // Only set error if it's not a cancellation
        if (err.name !== "CanceledError") {
          setError("Failed to fetch players");
        }
        // For cancelled requests, just clear the loading state
        // but don't set an error
      } finally {
        setLoading(false);
      }
    };

    fetchPlayers();

    return () => controller.abort();
  }, []);

  // Updated mock data with correct images

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFile({
        name: file.name,
        size: "1mb",
      });
      setFileForApi(file);
    }
  };

  // Handle player selection
  const handlePlayerSelect = (player) => {
    setSelectedPlayer(player);
    // Keep the player accordion open so users can change selection
    // Only open document accordion if user wants to proceed
    // setIsPlayerAccordionOpen(false);
    // setIsDocumentAccordionOpen(true);
  };

  const handleConfirmPitch = async () => {
    if (!selectedPlayer || !tenderData) return;
    console.log(fileForApi, "fileForApi");
    const wyscoutId = selectedPlayer.wyscout_id || selectedPlayer.ws_player_id;
    const formData = {
      tender_id: tenderData.id,
      ...(wyscoutId ? { wyscout_id: wyscoutId } : { player_id: selectedPlayer.account_id }),
      content:
        pitchContent ||
        `Pitching ${
          selectedPlayer.first_name && selectedPlayer.last_name
            ? `${selectedPlayer.first_name} ${selectedPlayer.last_name}`
            : selectedPlayer.first_name ||
              selectedPlayer.last_name ||
              "this player"
        } for ${formatPosition(tenderData.position)} position`,
      is_boosted: false,
      // The agent's OWN answer to "free agent?", always transmitted — this is
      // the switch below, not an inference. It used to stay in the browser,
      // and the club side then had to guess from a missing fee: an agent who
      // simply typed no amount had their player announced as fee-free, which
      // is a claim about a player that nobody made. Sent for false as well as
      // true; only that makes "no fee entered, switch untouched" distinguishable
      // from a real free agent.
      //
      // On a free-players-only request the switch is not on screen, so the
      // answer comes from the request itself: such a pitch is fee-free by
      // definition of what was asked for. Sent as true so the stored column
      // agrees with the verdict every surface renders instead of sitting NULL
      // beside it.
      is_free_agent: isFreePlayersOnlyRequest ? true : isFreePlayer,
    };

    // Fee + currency travel only when there IS a fee and the pitch is not a
    // free-agent one. A free-agent pitch carries neither — not even a currency,
    // which is why EUR is a form pre-selection and not a model default.
    // On a free-players-only request neither input is rendered, so there is
    // nothing to attach; the condition states that outright rather than relying
    // on the state happening to be empty.
    if (!isFreePlayersOnlyRequest && !isFreePlayer && transferFee) {
      formData.transfer_fee = transferFee;
      formData.transfer_fee_currency = selectedCurrency;
    }

    if (uploadedFile) {
      // Add file handling logic here if needed
      formData.document = fileForApi;
    }

    try {
      setIsLoading(true);
      setPitchError(null);
      const response = await tenderService.createPitch(formData);
      if (response.success) {
        setShowConfirmModal(false);
        setShowSuccessModal(true);
      } else {
        // INLINE, not a toast. The confirm modal stays open on failure, so a
        // toast fired over it and vanished before the agent had read it —
        // which is no way to deliver the two block messages this surface owes:
        // "A pitch for {player} is currently open on this request." and
        // "{player} cannot be pitched to this request again before {date}."
        // (PART A: "Blocked attempts show a clear inline message in two
        // variants"). Same block pattern as the sibling submit flow in
        // pages/Staff/StaffPitchTender.js, so it is not a new visual language.
        setPitchError(response.error);
      }
    } catch (error) {
      console.error("Pitch creation error:", error);
      setPitchError("Failed to create pitch");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate("/agent/tenders");
  };

  const handleViewPitches = () => {
    navigate("/agent/tenders", { state: { activeTab: "applied" } });
  };

  const handleTransferFeeChange = (e) => {
    // Get the raw input value
    const inputValue = e.target.value;
    // Remove all non-numeric characters
    const numericValue = inputValue.replace(/[^0-9]/g, "");
    // Store the raw numeric value in state
    setTransferFee(numericValue);
  };

  const handleConfirmClick = () => {
    if (selectedPlayer) {
      // A block message is about ONE player on ONE request. Re-opening the
      // dialog is a fresh attempt, so the old verdict must not still be on
      // screen — especially after the agent switched to a different player,
      // which is exactly what both messages tell them to do.
      setPitchError(null);
      setShowConfirmModal(true);
    }
  };

  // Deep-link fetch in flight — show a loading state instead of an
  // empty form (matches the page's plain "Loading players..." pattern).
  if (tenderLoading && !tenderData) {
    return (
      <div className="min-h-screen bg-white py-6 flex items-center justify-center">
        <p>Loading request...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-6">
      {/* Header */}
      {isMobile ? (
        // Mobile Header
        <div className="flex flex-col items-start justify-between mb-6 px-4 gap-4">
          <div className="flex items-center gap-2">
            <button onClick={() => navigate(-1)} className="text-gray-600">
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="#2C2C2C"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
            </button>
            <h1 className="text-lg font-semibold text-[#202630]">Pitch Player</h1>
          </div>
          <div className="flex gap-3 w-full">
            <button
              onClick={() => navigate(-1)}
              className="px-3 py-2 text-sm text-[#1E1E1E] hover:bg-gray-100 rounded-lg flex-1"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmClick}
              className={`px-3 py-2 text-sm rounded-lg flex-1 ${
                selectedPlayer
                  ? "bg-[#2283FF] text-white hover:bg-blue-600"
                  : "bg-[#2283FF4D] text-white cursor-not-allowed"
              }`}
            >
              {isLoading ? "Confirming..." : "Confirm"}
            </button>
          </div>
        </div>
      ) : (
        // Desktop Header (unchanged)
        <div className="flex items-center justify-between mb-8 px-14">
          <div className="flex items-center gap-2">
            <button onClick={() => navigate(-1)} className="text-gray-600">
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="#2C2C2C"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
            </button>
            <h1 className="text-xl font-semibold text-[#202630]">Pitch Player</h1>
          </div>
          <div className="flex gap-4">
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 text-[#1E1E1E] hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmClick}
              className={`px-4 py-2 rounded-lg ${
                selectedPlayer
                  ? "bg-[#2283FF] text-white hover:bg-blue-600"
                  : "bg-[#2283FF4D] text-white cursor-not-allowed"
              }`}
            >
              {isLoading ? "Confirming..." : "Confirm"}
            </button>
          </div>
        </div>
      )}

      {/* Tender Details */}
      {tenderData && (
        isMobile ? (
          // Mobile Tender Details
          <div className="px-4 bg-gradient-to-r from-[rgba(244,227,237,0.1)] to-[rgba(246,167,85,0.1)] border-y border-[#D2D4D880] p-4 mb-4">
            <div className="flex flex-col items-start justify-between mb-4 gap-4">
              <div className="flex items-center gap-2">
                <div>
                  {/* club.logo is null for every club without a linked
                      Wyscout team — the normal state of a self-registered
                      club, and this header rendered <img src={null}> for it.
                      Same initials badge as TenderCard.js:571-578. */}
                  {tenderData.club.logo ? (
                    <img
                      src={tenderData.club.logo}
                      alt={tenderData.club.name}
                      className="w-10 h-10"
                    />
                  ) : (
                    <div
                      className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-[#E9EEF6] text-[13px] font-bold text-[#6B7280]"
                      aria-label={
                        tenderData.club.name
                          ? `${tenderData.club.name} crest`
                          : "Club crest"
                      }
                    >
                      {clubInitials(tenderData.club.name)}
                    </div>
                  )}
                </div>
                <div>
                  <h2 className="text-base text-[#222A35] font-semibold mb-1">
                    Request for {formatPosition(tenderData.position)}
                  </h2>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[#222A35]">
                      By {tenderData.club.name}
                    </span>
                  </div>
                </div>
              </div>
              {/* Renders only when the request has a document (QAV5-402
                  Nebenbefund: used to render with an empty href when there
                  was nothing to open). */}
              {tenderData.document_url && (
                <a href={tenderData.document_url} target="_blank" rel="noreferrer" className="py-2 px-4 hover:bg-gray-100 bg-white rounded-lg self-start">
                  <svg
                    width="11"
                    height="20"
                    viewBox="0 0 11 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5.5 20C3.96667 20 2.66667 19.4667 1.6 18.4C0.533333 17.3333 0 16.0333 0 14.5V4C0 2.9 0.391667 1.95833 1.175 1.175C1.95833 0.391667 2.9 0 4 0C5.1 0 6.04167 0.391667 6.825 1.175C7.60833 1.95833 8 2.9 8 4V13.5C8 14.2 7.75833 14.7917 7.275 15.275C6.79167 15.7583 6.2 16 5.5 16C4.8 16 4.20833 15.7583 3.725 15.275C3.24167 14.7917 3 14.2 3 13.5V4H4.5V13.5C4.5 13.7833 4.59583 14.0208 4.7875 14.2125C4.97917 14.4042 5.21667 14.5 5.5 14.5C5.78333 14.5 6.02083 14.4042 6.2125 14.2125C6.40417 14.0208 6.5 13.7833 6.5 13.5V4C6.5 3.3 6.25833 2.70833 5.775 2.225C5.29167 1.74167 4.7 1.5 4 1.5C3.3 1.5 2.70833 1.74167 2.225 2.225C1.74167 2.70833 1.5 3.3 1.5 4V14.5C1.5 15.6 1.89167 16.5417 2.675 17.325C3.45833 18.1083 4.4 18.5 5.5 18.5C6.6 18.5 7.54167 18.1083 8.325 17.325C9.10833 16.5417 9.5 15.6 9.5 14.5V4H11V14.5C11 16.0333 10.4667 17.3333 9.4 18.4C8.33333 19.4667 7.03333 20 5.5 20Z"
                      fill="#1D1B20"
                    />
                  </svg>
                </a>
              )}
            </div>

            <p className="text-sm text-[#535370] mb-4">
              {tenderData.description || "Description Not Available"}
            </p>

            <div className="bg-white grid grid-cols-1 gap-4 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <p className="text-xs text-secondary">
                  {feeBlock.label || "Transfer Fee"}
                </p>
                <p className="font-medium text-sm text-right">
                  {feeBlock.value || "-"}
                </p>
              </div>
              <div className="flex justify-between items-center">
                <p className="text-xs text-secondary">Request Type</p>
                <span className="px-2 py-1 bg-emerald-100 text-emerald-600 rounded-full text-xs">
                  {tenderData.type_of_tender}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <p className="text-xs text-secondary">Games played in last season</p>
                <p className="font-medium text-sm">{tenderData.matches_played_last_season || "-"}</p>
              </div>
              <div className="flex justify-between items-center">
                <p className="text-xs text-secondary">Age Limit</p>
                <p className="font-medium text-sm">{tenderData.age || "-"}</p>
              </div>
              <div className="flex justify-between items-center">
                <p className="text-xs text-secondary">Foot</p>
                <p className="font-medium text-sm">{formatFoot(tenderData.foot) || "-"}</p>
              </div>
              <div className="flex justify-between items-center">
                <p className="text-xs text-secondary">Min. Height</p>
                <p className="font-medium text-sm">
                  {tenderData.minimum_height ? `${tenderData.minimum_height} cm` : "-"}
                </p>
              </div>
              <div className="flex justify-between items-center">
                <p className="text-xs text-secondary">
                  {getSalaryKind(tenderData) || "Net"} Monthly salary
                </p>
                <p className="font-medium text-sm text-right">
                  {formatSalary(tenderData) || "-"}
                </p>
              </div>
            </div>
          </div>
        ) : (
          // Desktop Tender Details (unchanged)
          <div className="px-14 bg-gradient-to-r from-[rgba(244,227,237,0.1)] to-[rgba(246,167,85,0.1)] border-y border-[#D2D4D880] p-6 mb-4">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <div>
                  {/* Same missing-crest case as the mobile header above. */}
                  {tenderData.club.logo ? (
                    <img
                      src={tenderData.club.logo}
                      alt={tenderData.club.name}
                      className="w-12 h-12"
                    />
                  ) : (
                    <div
                      className="w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-full bg-[#E9EEF6] text-[15px] font-bold text-[#6B7280]"
                      aria-label={
                        tenderData.club.name
                          ? `${tenderData.club.name} crest`
                          : "Club crest"
                      }
                    >
                      {clubInitials(tenderData.club.name)}
                    </div>
                  )}
                </div>
                <div>
                  <h2 className="text-lg text-[#222A35] font-semibold mb-1">
                    Request for {formatPosition(tenderData.position)}
                  </h2>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[#222A35]">
                      By {tenderData.club.name}
                    </span>
                  </div>{" "}
                </div>
              </div>
              {/* Renders only when the request has a document (QAV5-402
                  Nebenbefund: used to render with an empty href when there
                  was nothing to open). */}
              {tenderData.document_url && (
                <a href={tenderData.document_url} target="_blank" rel="noreferrer" className="py-2 px-4 hover:bg-gray-100 bg-white rounded-lg self-start">
                  <svg
                    width="11"
                    height="20"
                    viewBox="0 0 11 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5.5 20C3.96667 20 2.66667 19.4667 1.6 18.4C0.533333 17.3333 0 16.0333 0 14.5V4C0 2.9 0.391667 1.95833 1.175 1.175C1.95833 0.391667 2.9 0 4 0C5.1 0 6.04167 0.391667 6.825 1.175C7.60833 1.95833 8 2.9 8 4V13.5C8 14.2 7.75833 14.7917 7.275 15.275C6.79167 15.7583 6.2 16 5.5 16C4.8 16 4.20833 15.7583 3.725 15.275C3.24167 14.7917 3 14.2 3 13.5V4H4.5V13.5C4.5 13.7833 4.59583 14.0208 4.7875 14.2125C4.97917 14.4042 5.21667 14.5 5.5 14.5C5.78333 14.5 6.02083 14.4042 6.2125 14.2125C6.40417 14.0208 6.5 13.7833 6.5 13.5V4C6.5 3.3 6.25833 2.70833 5.775 2.225C5.29167 1.74167 4.7 1.5 4 1.5C3.3 1.5 2.70833 1.74167 2.225 2.225C1.74167 2.70833 1.5 3.3 1.5 4V14.5C1.5 15.6 1.89167 16.5417 2.675 17.325C3.45833 18.1083 4.4 18.5 5.5 18.5C6.6 18.5 7.54167 18.1083 8.325 17.325C9.10833 16.5417 9.5 15.6 9.5 14.5V4H11V14.5C11 16.0333 10.4667 17.3333 9.4 18.4C8.33333 19.4667 7.03333 20 5.5 20Z"
                      fill="#1D1B20"
                    />
                  </svg>
                </a>
              )}
            </div>

            <p className="text-sm text-[#535370] mb-6">
              {tenderData.description || "Description Not Available"}
            </p>

            <div className="flex bg-white justify-between gap-4">
              <div className=" p-4 rounded-lg flex flex-col gap-4">
                <p className="text-sm text-secondary">
                  {feeBlock.label || "Transfer Fee"}
                </p>
                <p className="font-medium">{feeBlock.value || "-"}</p>
              </div>
              <div className="bg-white p-4 rounded-lg flex flex-col gap-4">
                <p className="text-sm text-secondary">Request Type</p>
                <span className="px-3 py-1 bg-emerald-100 text-emerald-600 rounded-full text-sm">
                  {tenderData.type_of_tender}
                </span>
              </div>
              <div className="bg-white p-4 rounded-lg flex flex-col gap-4">
                <p className="text-sm text-secondary">
                  Games played in last season
                </p>
                <p className="font-medium">
                  {tenderData.matches_played_last_season || "-"}
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg flex flex-col gap-4">
                <p className="text-sm text-secondary">Age Limit</p>
                <p className="font-medium">{tenderData.age || "-"}</p>
              </div>
              <div className="bg-white p-4 rounded-lg flex flex-col gap-4">
                <p className="text-sm text-secondary">Foot</p>
                <p className="font-medium">{formatFoot(tenderData.foot) || "-"}</p>
              </div>
              <div className="bg-white p-4 rounded-lg flex flex-col gap-4">
                <p className="text-sm text-secondary">Min. Height</p>
                <p className="font-medium">
                  {tenderData.minimum_height
                    ? `${tenderData.minimum_height} cm`
                    : "-"}
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg flex flex-col gap-4">
                <p className="text-sm text-secondary">
                  {getSalaryKind(tenderData) || "Net"} Monthly salary
                </p>
                <p className="font-medium">{formatSalary(tenderData) || "-"}</p>
              </div>
            </div>
          </div>
        )
      )}

      {/* Select Player Section */}
      {isMobile ? (
        // Mobile Player Selection
        <div className="bg-[#F8F9FB] border border-[#D6DBEA] rounded-lg p-4 mx-4 mb-6">
          <button
            onClick={() => setIsPlayerAccordionOpen(!isPlayerAccordionOpen)}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-4">
              <div className="w-8 h-8 bg-[#FFFFFF] border border-[#D6DBEA] rounded-full flex items-center justify-center">
                <span className="text-[#374DF5] text-base font-semibold">1</span>
              </div>
              <div className="flex flex-col gap-1 items-start">
                <h2 className="font-semibold text-sm">Select Player</h2>
                <p className="text-xs text-[#535370]">
                  Select the player you want to pitch for this request
                </p>
              </div>
            </div>
            <svg
              className={`w-4 h-4 transform transition-transform ${
                isPlayerAccordionOpen ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {isPlayerAccordionOpen && (
            <div className="mt-4">
              <p className="text-xs text-gray-600 mb-4">
                Select the player you want to pitch for this request
              </p>

              {loading && <p>Loading players...</p>}
              {error && <p className="text-red-500">{error}</p>}

              {/* Mobile Player Selection List */}
              <div className="space-y-3 ">
                {players.map((player) => (
                  <div
                    key={player.id}
                    className={`flex flex-col p-3 border rounded-lg cursor-pointer gap-3 bg-white ${
                      selectedPlayer?.id === player.id
                        ? "border-[#2283FF]"
                        : "border-gray-200"
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePlayerSelect(player);
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-4 h-4 rounded-full flex items-center justify-center ${
                          selectedPlayer?.id === player.id
                            ? "border-[#2283FF] border"
                            : "border-[#999999] border-2"
                        }`}
                      >
                        {selectedPlayer?.id === player.id && (
                          <div className="w-2 h-2 bg-[#2283FF] rounded-full" />
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        {/* player_image is null for a roster player who never
                            uploaded a photo, and this row rendered a bare
                            <img src={null}> — the broken-image glyph, in the
                            "Select Player" list the agent has to pick from. */}
                        <PlayerAvatar
                          src={player.player_image}
                          name={[player.first_name, player.last_name]
                            .filter(Boolean)
                            .join(" ")}
                          alt={player.first_name || player.last_name || "Player"}
                          className="w-8 h-8 rounded-full text-[11px]"
                        />
                        <div className="flex flex-col gap-1 items-start">
                          <p className="font-medium text-[#222A35] text-sm">
                            {player.first_name && player.last_name
                              ? `${player.first_name} ${player.last_name}`
                              : player.first_name ||
                                player.last_name ||
                                "Unknown Player"}
                          </p>
                          <p className="text-xs text-[#43526E]">
                            {player.position}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 ml-7">
                      <div className="flex flex-col gap-1 items-start">
                        <p className="text-xs text-[#242426B2]">Country</p>
                        <p className="text-xs text-[#242426]">
                          {player.country?.name || "N/A"}
                        </p>
                      </div>
                      <div className="flex flex-col gap-1 items-start">
                        <p className="text-xs text-[#242426B2]">Age</p>
                        <p className="text-xs text-[#242426]">
                          {player.age || "N/A"}
                        </p>
                      </div>
                      <div className="flex flex-col gap-1 items-start">
                        <p className="text-xs text-[#242426B2]">Foot</p>
                        <p className="text-xs text-[#242426]">
                          {player.foot || "N/A"}
                        </p>
                      </div>
                      <div className="flex flex-col gap-1 items-start">
                        <p className="text-xs text-[#242426B2]">Height</p>
                        <p className="text-xs text-[#242426]">
                          {player.height ? `${player.height} cm` : "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        // Desktop Player Selection (unchanged)
        <div className="bg-[#F8F9FB] border border-[#D6DBEA] rounded-lg p-8 mx-14 mb-6">
          <button
            onClick={() => setIsPlayerAccordionOpen(!isPlayerAccordionOpen)}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-6 ">
              <div className="w-9 h-9 bg-[#FFFFFF] border border-[#D6DBEA] rounded-full flex items-center justify-center">
                <span className="text-[#374DF5] text-lg font-semibold">1</span>
              </div>
              <div className="flex flex-col gap-1 items-start">
                <h2 className="font-semibold">Select Player</h2>
                <p className="text-sm text-[#535370]">
                  Select the player you want to pitch for this request
                </p>
              </div>
            </div>
            <svg
              className={`w-5 h-5 transform transition-transform ${
                isPlayerAccordionOpen ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {isPlayerAccordionOpen && (
            <div className="mt-6">
              <p className="text-sm text-gray-600 mb-6">
                Select the player you want to pitch for this request
              </p>

              {loading && <p>Loading players...</p>}
              {error && <p className="text-red-500">{error}</p>}

              {/* Player Selection List */}
              <div className="space-y-3 ">
                {players.map((player) => (
                  <div
                    key={player.id}
                    className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer bg-white ${
                      selectedPlayer?.id === player.id
                        ? "border-[#2283FF]"
                        : "border-gray-200"
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePlayerSelect(player);
                    }}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`w-5 h-5 rounded-full  flex items-center justify-center ${
                          selectedPlayer?.id === player.id
                            ? "border-[#2283FF] border"
                            : "border-[#999999] border-2"
                        }`}
                      >
                        {selectedPlayer?.id === player.id && (
                          <div className="w-3 h-3 bg-[#2283FF] rounded-full" />
                        )}
                      </div>
                      <div className="flex items-center gap-5">
                        {/* Same missing-photo case as the mobile row above. */}
                        <PlayerAvatar
                          src={player.player_image}
                          name={[player.first_name, player.last_name]
                            .filter(Boolean)
                            .join(" ")}
                          alt={player.first_name || player.last_name || "Player"}
                          className="w-10 h-10 rounded-full text-[13px]"
                        />
                        <div className="flex flex-col gap-3 items-start">
                          <p className="font-medium text-[#222A35] text-[16px]">
                            {player.first_name && player.last_name
                              ? `${player.first_name} ${player.last_name}`
                              : player.first_name ||
                                player.last_name ||
                                "Unknown Player"}
                          </p>
                          <p className="text-sm text-[#43526E]">
                            {player.position}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-3 items-start">
                      <p className="text-sm text-[#242426B2]">Country</p>
                      <p className="font-sm text-[#242426]    ">
                        {player.country?.name || "N/A"}
                      </p>
                    </div>
                    <div className="flex flex-col gap-3 items-start">
                      <p className="text-sm text-[#242426B2]">Age</p>
                      <p className="font-sm text-[#242426] ">
                        {player.age || "N/A"}
                      </p>
                    </div>
                    <div className="flex flex-col gap-3 items-start">
                      <p className="text-sm text-[#242426B2]">Foot</p>
                      <p className="font-sm text-[#242426] ">
                        {player.foot || "N/A"}
                      </p>
                    </div>
                    <div className="flex flex-col gap-3 items-start">
                      <p className="text-sm text-[#242426B2]">Height</p>
                      <p className="font-sm text-[#242426] ">
                        {player.height ? `${player.height} cm` : "N/A"}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Transfer Fee Section */}
      {isMobile ? (
        // Mobile Transfer Fee Section
        <div className="bg-[#F8F9FB] border border-[#D6DBEA] rounded-lg p-4 mx-4 mb-6">
          <button
            onClick={() => setIsTransferFeeAccordionOpen(!isTransferFeeAccordionOpen)}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-4">
              <div className="w-8 h-8 bg-[#FFFFFF] border border-[#D6DBEA] rounded-full flex items-center justify-center">
                <span className="text-[#374DF5] text-base font-semibold">2</span>
              </div>
              <div className="flex flex-col gap-1 items-start">
                <h2 className="font-semibold text-sm">
                  {isFreePlayersOnlyRequest
                    ? FREE_ONLY_SECTION_TITLE
                    : FEE_SECTION_TITLE}
                </h2>
                <p className="text-xs text-[#535370]">
                  {isFreePlayersOnlyRequest
                    ? FREE_ONLY_SECTION_SUBTITLE
                    : FEE_SECTION_SUBTITLE}
                </p>
              </div>
            </div>
            <svg
              className={`w-4 h-4 transform transition-transform ${
                isTransferFeeAccordionOpen ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {isTransferFeeAccordionOpen && (
            <div className="mt-4 space-y-4">
              {/* Fee block (mobile). Absent in full on a free-players-only
                  request — switch, currency and amount all — because such a
                  request has no fee to state; the section subtitle above says
                  so. Everything below only exists for a request that CAN carry
                  a fee. */}
              {!isFreePlayersOnlyRequest && (
                <>
              {/* Free Player toggle (mobile) — when active the Currency
                  and Transfer Fee inputs go disabled + greyed and the
                  values are dropped from the submit payload. */}
              <div className="flex items-center justify-between p-3 bg-white border border-[#E9E9E9] rounded-lg">
                <div className="flex flex-col pr-3">
                  <span className="text-sm font-medium text-[#000000]">
                    Free Player
                  </span>
                  <span className="text-xs text-[#535370]">
                    Toggle on if no transfer fee is required
                  </span>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={isFreePlayer}
                  onClick={() => handleFreePlayerToggle(!isFreePlayer)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                    isFreePlayer ? "bg-[#374DF5]" : "bg-[#D6DBEA]"
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                      isFreePlayer ? "translate-x-5" : "translate-x-0.5"
                    }`}
                  />
                </button>
              </div>
              {isFreePlayer && (
                <p className="text-xs text-[#535370] -mt-2">
                  No transfer fee required for free agents.
                </p>
              )}
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#000000] mb-2">
                    Currency
                  </label>
                  <CustomDropdown
                    options={CURRENCY_CHOICES}
                    value={selectedCurrency}
                    onChange={(value) => setSelectedCurrency(value)}
                    placeholder="Select currency"
                    displayProperty="label"
                    valueProperty="value"
                    searchEnabled={true}
                    searchPlaceholder="Search currency..."
                    disabled={isFreePlayer}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#000000] mb-2">
                    Transfer Fee
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={isFreePlayer ? "" : formatCurrency(transferFee)}
                      onChange={handleTransferFeeChange}
                      disabled={isFreePlayer}
                      className={`w-full px-3 py-2 border border-[#E9E9E9] rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm font-medium pr-12 transition-colors ${
                        isFreePlayer
                          ? "bg-[#F3F4F6] text-[#9CA3AF] cursor-not-allowed"
                          : "bg-white"
                      }`}
                      placeholder={
                        isFreePlayer ? "Not applicable" : "Enter transfer fee"
                      }
                      style={
                        isFreePlayer
                          ? undefined
                          : { color: transferFee ? "#000000" : "#00000099" }
                      }
                    />
                    <span
                      className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs ${
                        isFreePlayer ? "text-[#9CA3AF]" : "text-secondary"
                      }`}
                    >
                      {selectedCurrency}
                    </span>
                  </div>
                </div>
              </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-[#000000] mb-2">
                  Pitch Message (optional)
                </label>
                <textarea
                  value={pitchContent}
                  onChange={(e) => setPitchContent(e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-[#E9E9E9] rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 min-h-[80px] text-sm font-medium"
                  placeholder="Add a message to your pitch"
                  style={{ color: pitchContent ? "#000000" : "#00000099" }}
                />
              </div>
            </div>
          )}
        </div>
      ) : (
        // Desktop Transfer Fee Section (unchanged)
        <div className="bg-[#F8F9FB] border border-[#D6DBEA] rounded-lg p-8 mx-14 mb-6">
          <button
            onClick={() => setIsTransferFeeAccordionOpen(!isTransferFeeAccordionOpen)}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-6 ">
              <div className="w-9 h-9 bg-[#FFFFFF] border border-[#D6DBEA] rounded-full flex items-center justify-center">
                <span className="text-[#374DF5] text-lg font-semibold">2</span>
              </div>
              <div className="flex flex-col gap-1 items-start">
                <h2 className="font-semibold">
                  {isFreePlayersOnlyRequest
                    ? FREE_ONLY_SECTION_TITLE
                    : FEE_SECTION_TITLE}
                </h2>
                <p className="text-sm text-[#535370]">
                  {isFreePlayersOnlyRequest
                    ? FREE_ONLY_SECTION_SUBTITLE
                    : FEE_SECTION_SUBTITLE}
                </p>
              </div>
            </div>
            <svg
              className={`w-5 h-5 transform transition-transform ${
                isTransferFeeAccordionOpen ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {isTransferFeeAccordionOpen && (
            <div className="mt-6 space-y-6">
              {/* Fee block (desktop) — same rule as the mobile branch: on a
                  free-players-only request none of this is rendered, because
                  the request has no fee to state. */}
              {!isFreePlayersOnlyRequest && (
                <>
              {/* Free Player toggle (desktop) — see mobile branch for
                  why this is a frontend-only toggle (the backend's
                  transfer_fee field is already nullable). */}
              <div className="flex items-center justify-between p-4 bg-white border border-[#E9E9E9] rounded-lg">
                <div className="flex flex-col pr-3">
                  <span className="text-[16px] font-medium text-[#000000]">
                    Free Player
                  </span>
                  <span className="text-sm text-[#535370]">
                    Toggle on if no transfer fee is required for this player.
                  </span>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={isFreePlayer}
                  onClick={() => handleFreePlayerToggle(!isFreePlayer)}
                  className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors flex-shrink-0 ${
                    isFreePlayer ? "bg-[#374DF5]" : "bg-[#D6DBEA]"
                  }`}
                >
                  <span
                    className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform ${
                      isFreePlayer ? "translate-x-5" : "translate-x-0.5"
                    }`}
                  />
                </button>
              </div>
              {isFreePlayer && (
                <p className="text-sm text-[#535370] -mt-2">
                  No transfer fee required for free agents.
                </p>
              )}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-[16px] font-medium text-[#000000] mb-2">
                    Currency
                  </label>
                  <CustomDropdown
                    options={CURRENCY_CHOICES}
                    value={selectedCurrency}
                    onChange={(value) => setSelectedCurrency(value)}
                    placeholder="Select currency"
                    displayProperty="label"
                    valueProperty="value"
                    searchEnabled={true}
                    searchPlaceholder="Search currency..."
                    disabled={isFreePlayer}
                  />
                </div>
                <div>
                  <label className="block text-[16px] font-medium text-[#000000] mb-2">
                    Transfer Fee
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={isFreePlayer ? "" : formatCurrency(transferFee)}
                      onChange={handleTransferFeeChange}
                      disabled={isFreePlayer}
                      className={`w-full px-4 py-2.5 border border-[#E9E9E9] rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-[16px] font-medium transition-colors ${
                        isFreePlayer
                          ? "bg-[#F3F4F6] text-[#9CA3AF] cursor-not-allowed"
                          : "bg-white"
                      }`}
                      placeholder={
                        isFreePlayer ? "Not applicable" : "Enter transfer fee"
                      }
                      style={
                        isFreePlayer
                          ? undefined
                          : { color: transferFee ? "#000000" : "#00000099" }
                      }
                    />
                    <span
                      className={`absolute right-3 top-1/2 -translate-y-1/2 ${
                        isFreePlayer ? "text-[#9CA3AF]" : "text-secondary"
                      }`}
                    >
                      {selectedCurrency}
                    </span>
                  </div>
                </div>
              </div>
                </>
              )}

              <div>
                <label className="block text-[16px] font-medium text-[#000000] mb-2">
                  Pitch Message (optional)
                </label>
                <textarea
                  value={pitchContent}
                  onChange={(e) => setPitchContent(e.target.value)}
                  className="w-full px-4 py-2.5 bg-white border border-[#E9E9E9] rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 min-h-[100px] text-[16px] font-medium"
                  placeholder="Add a message to your pitch"
                  style={{ color: pitchContent ? "#000000" : "#00000099" }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add Documents Section */}
      {isMobile ? (
        // Mobile Documents Section
        <div className="bg-[#F8F9FB] border border-[#D6DBEA] rounded-lg p-4 mx-4 mb-6">
          <button
            onClick={() => setIsDocumentAccordionOpen(!isDocumentAccordionOpen)}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-4">
              <div className="w-8 h-8 bg-[#FFFFFF] border border-[#D6DBEA] rounded-full flex items-center justify-center">
                <span className="text-[#374DF5] text-base font-semibold">3</span>
              </div>
              <div className="flex flex-col gap-1 items-start">
                <h2 className="font-semibold text-sm">Add documents (optional)</h2>
                <p className="text-xs text-[#535370]">
                  Add relevant documents about the player if needed.
                </p>
              </div>
            </div>
            <svg
              className={`w-4 h-4 transform transition-transform ${
                isDocumentAccordionOpen ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {isDocumentAccordionOpen && (
            <div className="mt-4">
              <div className="bg-white border-2 border-dashed border-gray-300 rounded-lg p-4">
                {!uploadedFile && (
                  <input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    onChange={handleFileUpload}
                    accept=".pdf"
                  />
                )}
                <label htmlFor="file-upload" className="cursor-pointer block">
                  <div className="mb-4 flex flex-col gap-2">
                    <p className="text-sm font-medium text-[#292D32] mb-1">
                      Choose a file or drag & drop it here
                    </p>
                    <p className="text-sm font-medium text-[#1A212F99]">
                      PDFs only, up to 50MB
                    </p>
                  </div>
                  {!uploadedFile && (
                    <button
                      onClick={() => {
                        document.getElementById("file-upload").click();
                      }}
                      type="button"
                      className="border border-[#E6E6E6] hover:bg-gray-200 text-[#1E1E1E] px-4 py-2 rounded-lg font-medium transition-colors text-sm"
                    >
                      Upload file
                    </button>
                  )}
                </label>
                {uploadedFile && (
                  <div className="mt-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-3">
                          <span className="text-gray-700 text-sm">File uploaded:</span>
                          <div className="w-6 h-6 bg-red-500 rounded flex items-center justify-center">
                            <span className="text-xs font-medium text-white">
                              PDF
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 text-sm">
                              {uploadedFile.name}
                            </p>
                            <p className="text-xs text-gray-600">
                              {uploadedFile.size}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 bg-green-100 text-green-700 px-2 py-1 rounded-full self-start">
                          <svg
                            className="w-3 h-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          <span className="text-xs font-medium">
                            Upload Completed
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3">
                      <button
                        onClick={() => setUploadedFile(null)}
                        className="border border-red-500 text-red-500 hover:bg-red-50 px-3 py-2 rounded-lg font-medium transition-colors text-sm"
                      >
                        Remove & Upload new file
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        // Desktop Documents Section (unchanged)
        <div className="bg-[#F8F9FB] border border-[#D6DBEA] rounded-lg p-8 mx-14 mb-6">
          <button
            onClick={() => setIsDocumentAccordionOpen(!isDocumentAccordionOpen)}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-6 ">
              <div className="w-9 h-9 bg-[#FFFFFF] border border-[#D6DBEA] rounded-full flex items-center justify-center">
                <span className="text-[#374DF5] text-lg font-semibold">3</span>
              </div>
              <div className="flex flex-col gap-1 items-start">
                <h2 className="font-semibold">Add documents (optional)</h2>
                <p className="text-sm text-[#535370]">
                  Add relevant documents about the player if needed.
                </p>
              </div>
            </div>
            <svg
              className={`w-5 h-5 transform transition-transform ${
                isDocumentAccordionOpen ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {isDocumentAccordionOpen && (
            <div className="mt-6">
              <div className="bg-white border-2 border-dashed border-gray-300 rounded-lg p-8">
                {!uploadedFile && (
                  <input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    onChange={handleFileUpload}
                    accept=".pdf"
                  />
                )}
                <label htmlFor="file-upload" className="cursor-pointer block">
                  <div className="mb-4 flex gap-4">
                    <p className="text-[16px] font-medium text-[#292D32] mb-2">
                      Choose a file or drag & drop it here
                    </p>
                    <p className="text-[16px] font-medium text-[#1A212F99]">
                      PDFs only, up to 50MB
                    </p>
                  </div>
                  {!uploadedFile && (
                    <button
                      onClick={() => {
                        document.getElementById("file-upload").click();
                      }}
                      type="button"
                      className="border border-[#E6E6E6] hover:bg-gray-200 text-[#1E1E1E] px-6 py-2 rounded-lg font-medium transition-colors"
                    >
                      Upload file
                    </button>
                  )}
                </label>
                {uploadedFile && (
                  <div className="mt-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-gray-700">File uploaded:</span>
                          <div className="w-8 h-8 bg-red-500 rounded flex items-center justify-center">
                            <span className="text-xs font-medium text-white">
                              PDF
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {uploadedFile.name}
                            </p>
                            <p className="text-sm text-gray-600">
                              {uploadedFile.size}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1 rounded-full">
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          <span className="text-sm font-medium">
                            Upload Completed
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4">
                      <button
                        onClick={() => setUploadedFile(null)}
                        className="border border-red-500 text-red-500 hover:bg-red-50 px-4 py-2 rounded-lg font-medium transition-colors"
                      >
                        Remove & Upload new file
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        isMobile ? (
          // Mobile Confirmation Modal
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-40 p-4">
            <div className="bg-white rounded-lg p-4 max-w-md w-full">
              <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mx-auto mb-3">
                <svg
                  className="w-6 h-6 text-[#2283FF]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-center mb-2">
                Confirm pitch
              </h3>
              <p className="text-gray-600 text-center mb-4 text-sm">
                Are you sure you want to pitch{" "}
                {selectedPlayer?.first_name && selectedPlayer?.last_name
                  ? `${selectedPlayer.first_name} ${selectedPlayer.last_name}`
                  : selectedPlayer?.first_name ||
                    selectedPlayer?.last_name ||
                    "this player"}{" "}
                for this request?
              </p>
              {pitchError && (
                <div
                  role="alert"
                  className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg"
                >
                  <p className="text-red-600 text-sm">{pitchError}</p>
                </div>
              )}
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="flex-1 px-4 py-2.5 border rounded-lg hover:bg-gray-50 text-sm"
                >
                  Cancel
                </button>
                <button
                  disabled={isLoading}
                  onClick={handleConfirmPitch}
                  className="flex-1 px-4 py-2.5 bg-[#2283FF] text-white rounded-lg hover:bg-blue-600 text-sm"
                >
                  {isLoading ? "Confirming..." : "Confirm"}
                </button>
              </div>
            </div>
          </div>
        ) : (
          // Desktop Confirmation Modal (unchanged)
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-40">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-[#2283FF]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-center mb-2">
                Confirm pitch
              </h3>
              <p className="text-gray-600 text-center mb-6">
                Are you sure you want to pitch{" "}
                {selectedPlayer?.first_name && selectedPlayer?.last_name
                  ? `${selectedPlayer.first_name} ${selectedPlayer.last_name}`
                  : selectedPlayer?.first_name ||
                    selectedPlayer?.last_name ||
                    "this player"}{" "}
                for this request?
              </p>
              {pitchError && (
                <div
                  role="alert"
                  className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg"
                >
                  <p className="text-red-600 text-sm">{pitchError}</p>
                </div>
              )}
              <div className="flex gap-4">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  disabled={isLoading}
                  onClick={handleConfirmPitch}
                  className="flex-1 px-4 py-2 bg-[#2283FF] text-white rounded-lg hover:bg-blue-600"
                >
                  {isLoading ? "Confirming..." : "Confirm"}
                </button>
              </div>
            </div>
          </div>
        )
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        isMobile ? (
          // Mobile Success Modal
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-4 max-w-lg w-full">
              <h3 className="text-lg font-semibold text-center mb-2">
                Pitch applied successfully
              </h3>
              <p className="text-gray-600 text-center mb-2 text-sm">
                You've successfully applied for this request.
              </p>
              <p className="text-gray-600 text-center mb-4 text-sm">
                We will keep you informed once the club accepts your pitch.
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={handleViewPitches}
                  className="flex-1 px-4 py-2.5 border rounded-lg hover:bg-gray-50 text-sm"
                >
                  View applied pitches
                </button>
                <button
                  onClick={handleGoBack}
                  className="flex-1 px-4 py-2.5 bg-[#2283FF] text-white rounded-lg hover:bg-blue-600 text-sm"
                >
                  Go back to listed requests
                </button>
              </div>
            </div>
          </div>
        ) : (
          // Desktop Success Modal (unchanged)
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 ">
            <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
              <h3 className="text-xl font-semibold text-center mb-2">
                Pitch applied successfully
              </h3>
              <p className="text-gray-600 text-center mb-2">
                You've successfully applied for this request.
              </p>
              <p className="text-gray-600 text-center mb-6">
                We will keep you informed once the club accepts your pitch.
              </p>
              <div className="flex gap-4">
                <button
                  onClick={handleViewPitches}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  View applied pitches
                </button>
                <button
                  onClick={handleGoBack}
                  className="flex-1 px-4 py-2 bg-[#2283FF] text-white rounded-lg hover:bg-blue-600"
                >
                  Go back to listed requests
                </button>
              </div>
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default PitchPlayer;
